import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Stack, Button, Grid, Box, Tab, Tabs, IconButton } from '@mui/material';
import { Icon } from "@iconify/react";
import { TabPanel, TabContext } from '@mui/lab';
import { Controller, useForm } from 'react-hook-form';
import { ACTIONS } from '../../config/const.config';
import {
  LISTING_MOVIES_COLUMNS,
  LISTING_MOVIES_ACTIONS,
  LISTING_SERIES_COLUMNS,
  LISTING_SERIES_ACTIONS,
  LISTING_SHOWS_COLUMNS,
  LISTING_SHOWS_ACTIONS,
} from '../../config/module-configs/manage-content.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import ConfirmPopup from '../common/confirm-popup.component';
import SnackbarInfo from '../common/snackbar-info.component';
import validationRules from '../../utils/validations.util';
import {
  assignMovie,
  viewMoviesById,
  deleteAssignedMovieById,
  assignSeries,
  viewSeriesById,
  deleteAssignedSeriesById,
  assignShow,
  viewShowById,
  deleteAssignedShowById,
} from '../../services/categories.service';
import SearchableContent from '../form-elements/searchableContent.component';
import ROUTE_PATH from '../../config/routes.config';
import editUrl from '../../utils/url-edit.util';

const ManageCategoryContent = ({ id }) => {
  const moviesColumns = LISTING_MOVIES_COLUMNS;
  const moviesActions = LISTING_MOVIES_ACTIONS;
  const seriesColumns = LISTING_SERIES_COLUMNS;
  const seriesActions = LISTING_SERIES_ACTIONS;
  const showsColumns = LISTING_SHOWS_COLUMNS;
  const showsActions = LISTING_SHOWS_ACTIONS;
  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      categories: [],
    },
  });
  const [moviesOptions, setMoviesOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    filtersCounter: 0,
    categoryName: '',
  });
  const [seriesOptions, setSeriesOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    filtersCounter: 0,
    categoryName: '',
  });
  const [showOptions, setShowOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    filtersCounter: 0,
    categoryName: '',
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [tabSelected, setTabSelected] = useState('movie');

  const handleMoviePageChange = (v) => {
    setMoviesOptions({
      ...moviesOptions,
      page: v,
      reloadCounter: moviesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSeriesPageChange = (v) => {
    setSeriesOptions({
      ...seriesOptions,
      page: v,
      reloadCounter: seriesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleShowPageChange = (v) => {
    setShowOptions({
      ...showOptions,
      page: v,
      reloadCounter: showOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleMovieRowsPerPageChange = (v) => {
    setMoviesOptions({
      ...moviesOptions,
      rowsPerPage: v,
      page: 0,
      reloadCounter: moviesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSeriesRowsPerPageChange = (v) => {
    setSeriesOptions({
      ...seriesOptions,
      rowsPerPage: v,
      page: 0,
      reloadCounter: seriesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleShowRowsPerPageChange = (v) => {
    setShowOptions({
      ...showOptions,
      rowsPerPage: v,
      page: 0,
      reloadCounter: showOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleMovieSortingChange = (sortBy, sortOrder) => {
    if (moviesOptions.loading) return;
    setMoviesOptions({
      ...moviesOptions,
      sortBy,
      sortOrder,
      reloadCounter: moviesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSeriesSortingChange = (sortBy, sortOrder) => {
    if (seriesOptions.loading) return;
    setSeriesOptions({
      ...seriesOptions,
      sortBy,
      sortOrder,
      reloadCounter: seriesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleShowSortingChange = (sortBy, sortOrder) => {
    if (showOptions.loading) return;
    setShowOptions({
      ...showOptions,
      sortBy,
      sortOrder,
      reloadCounter: showOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleMovieRefreshData = () => {
    setMoviesOptions({
      ...moviesOptions,
      reloadCounter: moviesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSeriesRefreshData = () => {
    setSeriesOptions({
      ...seriesOptions,
      reloadCounter: seriesOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleShowRefreshData = () => {
    setShowOptions({
      ...showOptions,
      reloadCounter: showOptions.reloadCounter + 1,
      loading: true,
    });
  };
  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };
  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    if (tabSelected === 'movie') {
      handleMovieRefreshData();
    } else if (tabSelected === 'series') {
      handleSeriesRefreshData();
    } else {
      handleShowRefreshData();
    }
  };

  const handleDeleteMovieData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteAssignedMovieById(id, doAction.data.id)
      .then(() => {
        handleActionSuccess('Assigned movie deleted successfully.');
        handleMovieRefreshData();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleDeleteSeriesData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteAssignedSeriesById(id, doAction.data.id)
      .then(() => {
        handleActionSuccess('Assigned series deleted successfully.');
        handleSeriesRefreshData();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleDeleteShowData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteAssignedShowById(id, doAction.data.id)
      .then(() => {
        handleActionSuccess('Assigned show deleted successfully.');
        handleShowRefreshData();
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const onShowFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    const selectedShows = [];
    for (let i = 0; i < data.shows.length; i += 1) {
       selectedShows.push(data.shows[i].id);
    }
    const payload = {
      shows: selectedShows,
    };
    assignShow(id, payload)
      .then((res) => {
        if (res.data) {
          handleMovieRefreshData();
          handleActionSuccess('Shows assigned successfully.');
          setValue('shows', []);
        }
      })
      .catch((e) => e);

    setFormSubmitted(false);
  };

  const onSeriesFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    const selectedSeries = [];
    for (let i = 0; i < data.series.length; i += 1) {
      selectedSeries.push(data.series[i].id);
    }
    const payload = {
      series: selectedSeries,
    };
    assignSeries(id, payload)
      .then((res) => {
        if (res.data) {
          handleMovieRefreshData();
          handleActionSuccess('Series assigned successfully.');
          setValue('series', []);
        }
      })
      .catch((e) => e);

    setFormSubmitted(false);
  };
  const onMoviesFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    const selectedMovies = [];
    for (let i = 0; i < data.movies.length; i += 1) {
      selectedMovies.push(data.movies[i].id);
    }
    const payload = {
      movies: selectedMovies,
    };
    assignMovie(id, payload)
      .then((res) => {
        if (res.data) {
          handleMovieRefreshData();
          handleActionSuccess(' Movie assigned successfully.');
          setValue('movies', []);
        }
      })
      .catch((e) => e);

    setFormSubmitted(false);
  };

  const handleChangeTab = (event, newValue) => {
    setTabSelected(newValue);
  };

  useEffect(() => {
    const params = [];
    if (tabSelected === 'movie') {
      params.push(`page=${moviesOptions.page + 1}`);
      params.push(`perPage=${moviesOptions.rowsPerPage}`);
      if (moviesOptions.sortBy && moviesOptions.sortOrder) {
        params.push(`sortBy=${moviesOptions.sortBy}`);
        params.push(`sortOrder=${moviesOptions.sortOrder}`);
      }

      const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
      viewMoviesById(id, paramsQuery)
        .then((res) => {
          setMoviesOptions({
            ...moviesOptions,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            categoryName: res?.data?.categoryName || '',
            error: false,
          });
        })
        .catch(() => {
          setMoviesOptions({
            ...moviesOptions,
            loading: false,
            page: 0,
            totalRows: 0,
            rows: [],
            categoryName: '',
            error: true,
          });
        });
    } else if (tabSelected === 'series') {
      params.push(`page=${moviesOptions.page + 1}`);
      params.push(`perPage=${moviesOptions.rowsPerPage}`);
      if (moviesOptions.sortBy && moviesOptions.sortOrder) {
        params.push(`sortBy=${moviesOptions.sortBy}`);
        params.push(`sortOrder=${moviesOptions.sortOrder}`);
      }

      const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
      viewSeriesById(id, paramsQuery)
        .then((res) => {
          setSeriesOptions({
            ...seriesOptions,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            categoryName: res?.data?.categoryName || '',
            error: false,
          });
        })
        .catch(() => {
          setSeriesOptions({
            ...seriesOptions,
            loading: false,
            page: 0,
            totalRows: 0,
            rows: [],
            categoryName: '',
            error: true,
          });
        });
    } else {
        params.push(`page=${showOptions.page + 1}`);
      params.push(`perPage=${showOptions.rowsPerPage}`);
      if (showOptions.sortBy && showOptions.sortOrder) {
        params.push(`sortBy=${showOptions.sortBy}`);
        params.push(`sortOrder=${showOptions.sortOrder}`);
      }

      const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
      viewShowById(id, paramsQuery)
        .then((res) => {
          setShowOptions({
            ...showOptions,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            categoryName: res?.data?.categoryName || '',
            error: false,
          });
        })
        .catch(() => {
          setShowOptions({
            ...showOptions,
            loading: false,
            page: 0,
            totalRows: 0,
            rows: [],
            categoryName: '',
            error: true,
          });
        });
    }
  }, [moviesOptions.reloadCounter, seriesOptions.reloadCounter, showOptions.reloadCounter, tabSelected]);

  const handleOpenPage = () => {
    const url = editUrl(ROUTE_PATH.CATEGORY_CONTENT_ORDER, id);
    navigate(url);
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 1, py: 1 }}
      >
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, py: 1 }}
          >
	        <Link to={ROUTE_PATH.CONTENT_CATEGORIES}>
							<IconButton color="primary" variant="contained" >
                <Icon icon="material-symbols:arrow-back-ios-new" />
              </IconButton>
	          </Link>
	            <Box sx={{marginLeft:0.2}} fontWeight="bold" fontSize="20px">{`Manage Content ${
	              moviesOptions.categoryName ? `- ${moviesOptions.categoryName}` : ''
	            }`}</Box> 
        </Stack>
        
        <Button
          color="inherit"
          variant="contained"
          onClick={() => handleOpenPage()}
          disabled={
            formSubmitted ||
            (moviesOptions.totalRows === 0 && seriesOptions.totalRows === 0 && showOptions.totalRows === 0)
          }
        >
          Manage Content Order
        </Button>        
      </Stack>

      <TabContext value={tabSelected}>
        <Box sx={{ width: '100%' }}>
          <Tabs value={tabSelected} onChange={handleChangeTab} centered>
            <Tab label="Movies" value="movie" />
            <Tab label="Series" value="series" />
            <Tab label="Shows" value="show" />
          </Tabs>
        </Box>
        <TabPanel value="movie">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, py: 1 }}
          >
            <Box
              width="50%"
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
            >
              <Box width="90%">
                <form
                  id="add-edit-movie-form"
                  onSubmit={handleSubmit(onMoviesFormSubmit)}
                >
                  <Grid item xs={12} sm={12} md={12}>
                    <Controller
                      name="movies"
                      control={control}
                      rules={validationRules.REQUIRED}
                      defaultValue={[]}
                      render={({ field: { onChange, value } }) => (
                        <SearchableContent
                          id="movies"
                          name="movies"
                          label="Movies"
                          defaultValue={value}
                          multiple
                          onChange={onChange}
                          sx={{ width: '100%', my: 1 }}
                          error={errors?.movies?.message || ''}
                          contentType="movie"
                        />
                      )}
                    />
                  </Grid>
                </form>
              </Box>

              <Button
                type="submit"
                color="primary"
                form="add-edit-movie-form"
                variant="contained"
                startIcon={<Iconify icon="ic:round-add" />}
                disabled={formSubmitted}
                sx={{ marginLeft: 2 }}
              >
                Assign
              </Button>
            </Box>

            <Box
              width="40%"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                color="inherit"
                variant="contained"
                startIcon={<Iconify icon="ic:twotone-refresh" />}
                onClick={handleMovieRefreshData}
                disabled={moviesOptions.loading}
              >
                Refresh
              </Button>
            </Box>
          </Stack>

          <Card>
            <ListData
              columns={moviesColumns}
              rows={moviesOptions.rows}
              page={moviesOptions.page}
              rowsPerPage={moviesOptions.rowsPerPage}
              totalRows={moviesOptions.totalRows}
              loading={moviesOptions.loading}
              actions={moviesActions}
              error={moviesOptions.error}
              sortBy={moviesOptions.sortBy}
              sortOrder={moviesOptions.sortOrder}
              onPageChange={handleMoviePageChange}
              onRowsPerPageChange={handleMovieRowsPerPageChange}
              onSortChange={handleMovieSortingChange}
              onAction={handleActionSelect}
            />
          </Card>

          {doAction.action === ACTIONS.DELETE.value && (
            <ConfirmPopup
              title="Delete Assigned Movies"
              message="Do you want to delete this Movie? You can not undo this action!"
              noteText=""
              onCancel={handleActionCancel}
              onSuccess={handleDeleteMovieData}
            />
          )}
        </TabPanel>
        <TabPanel value="series">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, py: 1 }}
          >
            <Box
              width="50%"
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
            >
              <Box width="90%">
                <form
                  id="add-edit-series-form"
                  onSubmit={handleSubmit(onSeriesFormSubmit)}
                >
                  <Grid item xs={12} sm={12} md={12}>
                    <Controller
                      name="series"
                      control={control}
                      rules={validationRules.REQUIRED}
                      defaultValue={[]}
                      render={({ field: { onChange, value } }) => (
                        <SearchableContent
                          id="series"
                          name="series"
                          label="Series"
                          defaultValue={value}
                          multiple
                          onChange={onChange}
                          sx={{ width: '100%', my: 1 }}
                          error={errors?.series?.message || ''}
                          contentType="series"
                        />
                      )}
                    />
                  </Grid>
                </form>
              </Box>

              <Button
                type="submit"
                color="primary"
                form="add-edit-series-form"
                variant="contained"
                startIcon={<Iconify icon="ic:round-add" />}
                disabled={formSubmitted}
                sx={{ marginLeft: 2 }}
              >
                Assign
              </Button>
            </Box>

            <Box
              width="40%"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                color="inherit"
                variant="contained"
                startIcon={<Iconify icon="ic:twotone-refresh" />}
                onClick={handleSeriesRefreshData}
                disabled={seriesOptions.loading}
              >
                Refresh
              </Button>
            </Box>
          </Stack>

          <Card>
            <ListData
              columns={seriesColumns}
              rows={seriesOptions.rows}
              page={seriesOptions.page}
              rowsPerPage={seriesOptions.rowsPerPage}
              totalRows={seriesOptions.totalRows}
              loading={seriesOptions.loading}
              actions={seriesActions}
              error={seriesOptions.error}
              sortBy={seriesOptions.sortBy}
              sortOrder={seriesOptions.sortOrder}
              onPageChange={handleSeriesPageChange}
              onRowsPerPageChange={handleSeriesRowsPerPageChange}
              onSortChange={handleSeriesSortingChange}
              onAction={handleActionSelect}
            />
          </Card>

          {doAction.action === ACTIONS.DELETE.value && (
            <ConfirmPopup
              title="Delete Assigned Series"
              message="Do you want to delete this series? You can not undo this action!"
              noteText=""
              onCancel={handleActionCancel}
              onSuccess={handleDeleteSeriesData}
            />
          )}
        </TabPanel>
        <TabPanel value="show">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 1, py: 1 }}
          >
            <Box
              width="50%"
              display="flex"
              justifyContent="flex-start"
              alignItems="center"
            >
              <Box width="90%">
                <form
                  id="add-edit-series-form"
                  onSubmit={handleSubmit(onShowFormSubmit)}
                >
                  <Grid item xs={12} sm={12} md={12}>
                    <Controller
                      name="shows"
                      control={control}
                      rules={validationRules.REQUIRED}
                      defaultValue={[]}
                      render={({ field: { onChange, value } }) => (
                        <SearchableContent
                          id="shows"
                          name="shows"
                          label="Shows"
                          defaultValue={value}
                          multiple
                          onChange={onChange}
                          sx={{ width: '100%', my: 1 }}
                          error={errors?.series?.message || ''}
                          contentType="shows"
                        />
                      )}
                    />
                  </Grid>
                </form>
              </Box>

              <Button
                type="submit"
                color="primary"
                form="add-edit-series-form"
                variant="contained"
                startIcon={<Iconify icon="ic:round-add" />}
                disabled={formSubmitted}
                sx={{ marginLeft: 2 }}
              >
                Assign
              </Button>
            </Box>

            <Box
              width="40%"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
            >
              <Button
                color="inherit"
                variant="contained"
                startIcon={<Iconify icon="ic:twotone-refresh" />}
                onClick={handleSeriesRefreshData}
                disabled={showOptions.loading}
              >
                Refresh
              </Button>
            </Box>
          </Stack>

          <Card>
            <ListData
              columns={showsColumns}
              rows={showOptions.rows}
              page={showOptions.page}
              rowsPerPage={showOptions.rowsPerPage}
              totalRows={showOptions.totalRows}
              loading={showOptions.loading}
              actions={showsActions}
              error={showOptions.error}
              sortBy={showOptions.sortBy}
              sortOrder={showOptions.sortOrder}
              onPageChange={handleShowPageChange}
              onRowsPerPageChange={handleShowRowsPerPageChange}
              onSortChange={handleShowSortingChange}
              onAction={handleActionSelect}
            />
          </Card>

          {doAction.action === ACTIONS.DELETE.value && (
            <ConfirmPopup
              title="Delete Assigned Show"
              message="Do you want to delete this show? You can not undo this action!"
              noteText=""
              onCancel={handleActionCancel}
              onSuccess={handleDeleteShowData}
            />
          )}
        </TabPanel>
      </TabContext>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ManageCategoryContent.propTypes = {
  id: PropTypes.string.isRequired,
};

export default ManageCategoryContent;
